import { Outlet, NavLink } from "react-router-dom";
import { LogOut, LayoutDashboard, Ship } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { ClientProfileGate } from "@/components/ClientProfileGate";

/**
 * Shell for `client` role — and for staff/admin on mobile via the
 * dedicated mobile router. Two chrome states:
 *
 *   • Desktop (md+): top bar with logo + inline nav + sign-out.
 *   • Mobile (< md): condensed sticky header + FIXED BOTTOM NAV with
 *     big touch targets. `pb-24` on the main scroll area prevents the
 *     nav from overlapping content.
 *
 * The nav items themselves are role-aware: clients get Dashboard +
 * Vessels; staff on mobile get Vessels + Quotes (view-only). The
 * caller controls which item set to pass in via the `navItems` prop
 * (defaults to the client set) so the same shell renders for both.
 */
const CLIENT_NAV = [
  { to: "/", label: "Home", icon: LayoutDashboard, testid: "client-nav-dashboard", end: true },
  { to: "/vessels", label: "Vessels", icon: Ship, testid: "client-nav-vessels" },
];

export default function ClientLayout({ navItems = CLIENT_NAV, brandLabel = "Client Portal" }) {
  const { user, logout } = useAuth();
  // Onboarding gate — clients must finish their profile card (name,
  // company, phone) before any portal page is reachable. Skipped for
  // admins/staff using "Preview as client" (`_preview` flag).
  const needsProfile =
    user?.role === "client" && !user?._preview && user?.profile_complete === false;

  return (
    <div className="min-h-screen w-full bg-[#F6F7F8] text-[#011836] flex flex-col">
      {/* Sticky header — compressed on mobile, roomier on desktop. */}
      <header
        className="sticky top-0 z-40 flex items-center justify-between gap-3 px-4 sm:px-6 md:px-8 h-14 md:h-16 border-b border-white/[0.06] bg-[#011836]/85 backdrop-blur-xl"
        data-testid="client-topbar"
      >
        <div className="flex items-center gap-3 min-w-0">
          <Logo className="h-7 md:h-8 w-auto" showWordmark={false} />
          <div className="hidden sm:block min-w-0">
            <div className="font-display text-sm font-semibold tracking-wide text-white truncate">
              Southern Ocean Solutions
            </div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#66C2EA]">
              {brandLabel}
            </div>
          </div>
        </div>

        {/* Desktop-only inline nav — bottom nav takes over on mobile. */}
        <nav
          className="hidden md:flex items-center gap-2"
          aria-label="Primary navigation"
          data-testid="client-nav"
        >
          {navItems.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-display tracking-wide transition-colors ${
                    isActive
                      ? "bg-[#0088CC]/15 text-[#66C2EA] border border-[#0088CC]/40"
                      : "text-[#8FA6C0] hover:text-white border border-transparent"
                  }`
                }
              >
                <Icon className="h-3.5 w-3.5" />
                <span>{n.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="flex items-center gap-2 md:gap-3 min-w-0">
          <div className="hidden md:block text-right min-w-0">
            <div className="text-sm text-white truncate max-w-[180px]" data-testid="client-name">
              {user?.name || user?.email}
            </div>
            {user?.company && (
              <div className="text-[10px] uppercase tracking-wider text-[#8FA6C0] truncate max-w-[180px]">
                {user.company}
              </div>
            )}
          </div>
          <button
            onClick={logout}
            className="inline-flex items-center justify-center gap-2 h-9 md:h-10 px-3 md:px-4 rounded-full text-xs uppercase tracking-wider font-display font-semibold text-[#8FA6C0] hover:text-white hover:bg-white/5 border border-white/10 active:scale-[0.97] transition-transform"
            data-testid="client-logout-btn"
            aria-label="Sign out"
          >
            <LogOut className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Sign out</span>
          </button>
        </div>
      </header>

      {/* Content — bottom padding leaves room for the mobile nav bar. */}
      <main className="flex-1 pb-24 md:pb-0">
        {needsProfile ? <ClientProfileGate /> : <Outlet />}
      </main>

      {/* Mobile-only bottom tab bar. Uses `pb-safe` (Tailwind arbitrary
          util via `env(safe-area-inset-bottom)`) to sit above the home
          indicator on iOS. */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-50 border-t border-white/[0.08] bg-[#011836]/95 backdrop-blur-xl"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        aria-label="Mobile navigation"
        data-testid="client-mobile-nav"
      >
        <div className="flex items-stretch justify-around px-2 pt-2 pb-2">
          {navItems.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`${n.testid}-mobile`}
                className={({ isActive }) =>
                  `flex-1 flex flex-col items-center justify-center gap-1 py-1.5 rounded-xl mx-1 min-h-[52px] text-[11px] font-display font-semibold tracking-wide transition-colors ${
                    isActive
                      ? "bg-[#0088CC]/15 text-[#66C2EA]"
                      : "text-[#8FA6C0] active:bg-white/5 active:scale-[0.97]"
                  }`
                }
              >
                <Icon className="h-5 w-5" />
                <span>{n.label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
